import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Lightbulb, PenTool, MonitorUp } from 'lucide-react';

// ============================================================================
// DATA
// ============================================================================

const STEPS = [
  {
    icon: Lightbulb,
    number: '1',
    name: 'Discover, Design & Plan',
    desc: 'Understand users, goals, and requirements. Define success metrics, UX flows, and system architecture. Decisions made here compound for years.',
  },
  {
    icon: PenTool,
    number: '2',
    name: 'Develop & Test',
    desc: 'AI Assisted Development to create scalable, high-performance software with CI-CD pipelines, code review, and automated testing baked in.',
  },
  {
    icon: MonitorUp,
    number: '3',
    name: 'Launch & Optimize',
    desc: 'Deploy to production, monitor performance, and iterate based on adoption data. We handhold you through every stage of product adoption and success.'
  },
];

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fadeUpDelayed = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.1 } },
};

const stepsContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const stepVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const watermarkVariants = {
  hidden: { opacity: 0, scale: 1.1 },
  visible: { opacity: 0.12, scale: 1, transition: { duration: 0.8, delay: 0.2 } },
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const SectionEyebrow = ({ label }) => (
  <div className="appr-eyebrow">
    <span className="appr-eyebrow-line" />
    <span className="appr-eyebrow-text">{label}</span>
    <span className="appr-eyebrow-line" />
  </div>
);

const StepCard = ({ step }) => {
  const Icon = step.icon;

  return (
    <motion.div className="appr-step" variants={stepVariants}>
      {/* Icon + watermark number row */}
      <div className="appr-step-top">
        <motion.div
          className="appr-icon-box"
          whileHover={{ scale: 1.05, transition: { duration: 0.3, ease: 'easeOut' } }}
        >
          <Icon size={20} className="appr-icon" aria-hidden="true" />
        </motion.div>

        {/* Watermark number — spec allows inline fontSize here only */}
        <motion.span
          className="appr-watermark"
          style={{ fontSize: '120px' }}
          variants={watermarkVariants}
          aria-hidden="true"
        >
          {step.number}
        </motion.span>
      </div>

      <h3 className="appr-step-name">{step.name}</h3>
      <p className="appr-step-desc">{step.desc}</p>
    </motion.div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ApproachSection = () => {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-80px' });

  return (
    <section id="approach" className="approach-section" ref={sectionRef}>
      <div className="container">
        {/* Eyebrow */}
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={fadeUp}
        >
          <SectionEyebrow label="Our Approach" />
        </motion.div>

        {/* Headline + subtext */}
        <motion.div
          className="appr-header"
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={fadeUpDelayed}
        >
          <h2 className="appr-heading">From idea to product — our approach</h2>
          <p className="appr-subtext">
            A clear, predictable process that turns ambiguous ideas into shipping software.
            Timing hints set expectations so you always know where you are.
          </p>
        </motion.div>

        {/* 3-column steps — staggered */}
        <motion.div
          className="appr-steps-grid"
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={stepsContainerVariants}
        >
          {STEPS.map((step) => (
            <StepCard key={step.name} step={step} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ApproachSection;
